import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "@/lib/auth";
import { captureReferralFromUrl } from "@/lib/vendorReferral";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import UserDashboard from "@/pages/UserDashboard";
import NotFound from "@/pages/NotFound";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import ContactUs from "@/pages/ContactUs";
import MeshViewer from "@/pages/MeshViewer";
import TeamPage from "@/pages/TeamPage";
import AcceptInvite from "@/pages/AcceptInvite";
import CatalogPage from "@/pages/CatalogPage";
import PhotoRoomPage from "@/pages/PhotoRoomPage";
import HowItWorks from "@/pages/HowItWorks";
import ForJewellers from "@/pages/ForJewellers";
import ForDesigners from "@/pages/ForDesigners";
import About from "@/pages/About";
import Glossary from "@/pages/Glossary";
import GlossaryTerm from "@/pages/GlossaryTerm";
import Articles from "@/pages/Articles";
import Article from "@/pages/Article";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudy from "@/pages/CaseStudy";
import ToolLanding from "@/pages/ToolLanding";
import { TOOL_STYLE_LIST } from "@/data/toolLandings";
import StudentPortfolio from "@/pages/StudentPortfolio";
import VendorSignIn from "@/pages/VendorSignIn";
import VendorSignUp from "@/pages/VendorSignUp";
import VendorDashboard from "@/pages/VendorDashboard";
import { ADMIN_ROUTE, ADMIN_DASHBOARD_ROUTE } from "@/lib/admin";
import ConsentBanner from "@/components/ConsentBanner";
import { initAnalytics, trackPageView, isAnalyticsEnabled } from "@/lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The Google OAuth client ID comes from the
// REACT_APP_GOOGLE_CLIENT_ID env var only.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/**
 * ReferralCatcher — global affiliate-link listener.
 *
 * Vendor referral links land users on ANY route (`/`, `/for-jewellers`,
 * `/pricing`, …) with `?ref=CODE` in the query string. We used to capture
 * the code only when the user hit `/signup` — but if the user first
 * navigated within the marketing site the `?ref=` was stripped by
 * `<Link to="…">` clicks and the attribution was lost.
 *
 * Fix: on EVERY route mount + on every location change, if `?ref=` is
 * present in the URL, run `captureReferralFromUrl()`. That helper does
 * a server-side validation against `/api/vendor/public/referral/{code}`
 * (rate-limited 10/min/IP, alphabet-whitelisted) before writing the
 * 30-day `facetra_ref` cookie — so we can NEVER cache a junk code, and
 * the signup path stays byte-for-byte the same.
 *
 * Security:
 *   - Server validates code before caching (guard against attribution
 *     poisoning + vendor enumeration).
 *   - Cookie is `SameSite=Lax; Secure` on HTTPS.
 *   - Cookie cleared on successful signup (`clearReferralCookie` in
 *     SignUp / GoogleAuthButton) — no cross-user leak on shared devices.
 *   - Never runs on `/vendor/…` routes — those belong to the vendors
 *     themselves and a vendor viewing their own link should not
 *     over-write their session's cached ref.
 */
function ReferralCatcher() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/vendor")) return;
    if (!location.search || !location.search.includes("ref=")) return;
    // Fire-and-forget; helper is silent on failure and only writes the
    // cookie after backend validation. No await needed.
    captureReferralFromUrl();
  }, [location.pathname, location.search]);
  return null;
}

/**
 * SPA route tracker for GA4. GA4's auto-collected `page_view` fires
 * only on hard nav; React Router client-side transitions need this
 * explicit hit or dashboards under-count 40-60% of engagement. Consent
 * gating happens inside GA4 via Consent Mode v2 — we always push the
 * event, and GA4 stores it as a modeled hit if consent is still denied.
 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function AppRouter() {
  return (
    <>
      <ReferralCatcher />
      <RouteTracker />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app" element={<UserDashboard />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/3d-viewer/:jobId" element={<MeshViewer />} />
      <Route path="/enterprise/team" element={<TeamPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/catalog" element={<CatalogPage />} />
      <Route path="/photoroom" element={<PhotoRoomPage />} />
      {/* Canonical content pages — indexed for SEO/GEO */}
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/for-jewellers" element={<ForJewellers />} />
      <Route path="/for-designers" element={<ForDesigners />} />
      <Route path="/about" element={<About />} />
      <Route path="/glossary" element={<Glossary />} />
      <Route path="/glossary/:slug" element={<GlossaryTerm />} />
      <Route path="/articles" element={<Articles />} />
      <Route path="/articles/:slug" element={<Article />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/case-studies/:slug" element={<CaseStudy />} />
      {/* Per-style pillar landing pages — commercial-intent SEO targets.
          Phase 2 URL restructure: root-level slugs are canonical (e.g.
          /ai-kundan-jewellery-design). /tools/:style still resolves for
          back-compat and is dual-served with canonical pointing to the
          root URL to avoid duplicate-content penalties. */}
      <Route path="/tools/:style" element={<ToolLanding />} />
      {TOOL_STYLE_LIST.map((slug) => (
        <Route
          key={`root-${slug}`}
          path={`/${slug}`}
          element={<ToolLanding />}
        />
      ))}
      {/* Dedicated demographic landing page for design students */}
      <Route path="/for-students" element={<StudentPortfolio />} />
      {/* Vendor / affiliate partner portal */}
      <Route path="/vendor/signup" element={<VendorSignUp />} />
      <Route path="/vendor/login" element={<VendorSignIn />} />
      <Route path="/vendor/dashboard" element={<VendorDashboard />} />
      <Route path={ADMIN_ROUTE} element={<AdminLogin />} />
      <Route path={ADMIN_DASHBOARD_ROUTE} element={<AdminDashboard />} />
      {/* Old guessable paths now masquerade as 404s. */}
      <Route path="/admin" element={<NotFound />} />
      <Route path="/admin/*" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#1A1A1A",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
          },
        }}
      />
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ""}>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </GoogleOAuthProvider>
      <ConsentBanner />
    </div>
  );
}

export default App;
